import { VITE_API_URL, } from "../ENV";

const withHost = (path: string,) => `${VITE_API_URL}${path}`;
const withParams = (path: string, params: Record<string, string | number | null | boolean | string[] | number[]>,) => {
  const query = Object.keys(params,)
    .filter(k => typeof params[k] !== "undefined",)
    .flatMap((k,) => {
      const value = params[k];
      if (Array.isArray(value,)) {
        return value.map(v => `${k}=${encodeURIComponent(String(v,),)}`,);
      }
      return `${k}=${encodeURIComponent(String(value,),)}`;
    },)
    .join("&",);

  return query ? `${path}?${query}` : `${path}?`;
};

const defaultHeaders = {
  headers: {
    "Content-Type": "application/json",
  },
};

/**
 * Provides a convenient way to build API URL and requests.
 *
 * queries - for GET requests.
 * mutations - for POST, PUT, DELETE requests.
 */
export const ApiBuilder = {
  queries: {
    cards: {
      /**
       * Обновление алиаса карты
       */
      index: {
        url: withHost("/v1/cards",),
      },
      binding: {
        /** Получение ссылки для привязки карты */
        link: {
          url: withHost("/v1/cards/binding/link",),
        },
        /** Получить статус привязки карты */
        status: {
          url: withHost("/v1/cards/binding/status",),
        },
      },
    },
    documents: {
      /** Получение списка документов */
      index: {
        url: withHost("/v1/documents",),
      },
      byId: (id: string,) => ({
        /** Получение ссылки на документ */
        url: {
          url: withHost(`/v1/documents/${id}/url`,),
        },
      }),
      /** Получение типа документов */
      types: {
        url: withHost("/v1/documents/types",),
      },
    },
    /** Получение списка макрокатегорий */
    macrocategories: {
      url: withHost("/v1/macrocategories",),
    },
    /** Получение списка гражданств */
    nationalities: {
      url: withHost("/v1/nationalities",),
    },
    offers: {
      /** Получение предложений по рассрочке. */
      index: {
        url: withHost("/v1/offers",),
      },
      /** Получение планов платежей */
      repaymentSchedules: {
        url: withHost("/v1/offers/repayment-schedules",),
      },
    },
    operations: {
      /** Получение списка операций клиента */
      index: (params: { startDate: string; endDate: string; perPage: number; page: number; creditorIds?: string },) => ({
        url: withHost(withParams("/v1/operations", params,),),
      }),
      byId: (id: string,) => ({
        /** Получение детальной информации по операции клиента */
        index: {
          url: withHost(`/v1/operations/${id}`,),
        },
        /** Получение комиссии по операции */
        commission: {
          url: withHost(`/v1/operations/${id}/commission`,),
        },
        /** Получение товарной корзины по операции клиента */
        products: {
          url: withHost(`/v1/operations/${id}/products`,),
        },
        /** Получение графика погашений (платежей) по операции клиента */
        repaymentSchedule: {
          url: withHost(`/v1/operations/${id}/repayment-schedule`,),
        },
      }),
      /** Получение графика платежей по всем операциям */
      repaymentSchedule: {
        url: withHost("/v1/operations/repayment-schedule",),
      },
      /** Получение лимита и остатков по нему для клиента */
      limits: {
        url: withHost("/v1/operations/limits",),
      },
      /** Получение трат и макрокатегорий по операциям клиента */
      macrocategories: {
        url: withHost("/v1/operations/macrocategories",),
      },
    },
    partners: {
      /** Поиск партнеров */
      index: {
        url: withHost("/v1/partners",),
      },
      byId: (id: string,) => ({
        /** Получение детальной информации по партнеру */
        details: {
          url: withHost(`/v1/partners/${id}/details`,),
        },
      }),
    },
    paymentRequests: {
      /** Проверка статуса выдачи заявки, созданной по QR */
      checking: {
        url: withHost("/v1/payment-requests/checking",),
      },
    },
    payments: {
      byPaymentId: (paymentId: string,) => ({
        /** Проверка статуса платежа */
        status: {
          url: withHost(`/v1/payments/${paymentId}/status`,),
        },
      }),
      /** Получение предстоящих платежей в текущем месяце */
      upcoming: {
        url: withHost("/v1/payments/upcoming",),
      },
    },
    /** Получение информации о пользователе */
    userProfiles: {
      url: withHost("/v1/user-profiles",),
    },
    limits: {
      /** Получение информации о лимитах */
      index: (params: { count: number; page: number; statuses?: string | string[]; creditorIds?: string | string[] },) => ({
        url: withHost(withParams("/v1/limits", params,),),
      }),
      byId: (id: string,) => ({
        /** Получение детальной информации о лимите клиента */
        index: {
          url: withHost(`/v1/limits/${id}`,),
        },
        /** Получение информации статуса о выпуске карты */
        cardIssuanceStatus: {
          url: withHost(`/v1/limits/${id}/payment-card`,),
        },
        /** Получение графика платежей по лимиту */
        paymentSchedule: {
          url: withHost(`/v1/limits/${id}/payment-schedule`,),
        },
        /** Получение информации доступности карты для выпуска */
        paymentCardAvailability: {
          url: withHost(`/v1/limits/${id}/payment-card/availability`,),
        },
        /** Получение ссылки на просмотр реквизитов карты */
        cardRequisites: {
          url: withHost(`/v1/limits/${id}/payment-card/requisites`,),
        },
      }),
    },
    paymentCards: {
      /** Получение информации об активных картах по лимитам */
      index: {
        url: withHost("/v1/payment-cards",),
      },
    },
  },
  mutations: {
    devices: (pin: string, fingerprint: string,) => ({
      ...defaultHeaders,
      url: withHost("/v1/devices",),
      method: "POST",
      body: { pincode: pin, fingerprint, },
    }),
    authenticateDevice: (pin: string, fingerprint: string,) => ({
      ...defaultHeaders,
      url: withHost("/v1/devices/authenticate",),
      method: "POST",
      body: { pincode: pin, fingerprint, },
    }),
    /** Создание обращения пользователя */
    appeals: (text: string,) => ({
      ...defaultHeaders,
      url: withHost("/v1/appeals",),
      method: "POST",
      body: { text, },
    }),
    cards: {
      /** Обновление алиаса карты */
      byId: (id: string, aliasName: string,) => ({
        ...defaultHeaders,
        url: withHost(`/v1/cards/${id}`,),
        method: "PUT",
        body: { aliasName, },
      }),
    },
    confirmationCode: {
      /** Проверка кода подтверждения */
      checking: (code: string,) => ({
        ...defaultHeaders,
        url: withHost("/v1/confirmation-code/checking",),
        method: "POST",
        body: { code, },
      }),
      /** Переотправка кода подтверждения */
      resending: () => ({
        ...defaultHeaders,
        url: withHost("/v1/confirmation-code/resending",),
        method: "POST",
        body: {},
      }),
    },
    offers: {
      byId: (id: string,) => ({
        /** Подтверждение выбора предложения. */
        confirmation: () => ({
          ...defaultHeaders,
          url: withHost(`/v1/offers/${id}/confirmation`,),
          method: "POST",
          body: {},
        }),
      }),
    },
    operations: {
      byId: (operationId: string,) => ({
        /** Отправка платежа */
        payments: (body: { paymentType: string; amount: number; paymentInstrumentId: number },) => ({
          ...defaultHeaders,
          url: withHost(`/v1/operations/${operationId}/payments`,),
          method: "POST",
          body,
        }),
      }),
    },
    otp: {
      /** Отправка смс кода */
      index: (phone: string,) => ({
        ...defaultHeaders,
        url: withHost("/v1/otp",),
        method: "POST",
        body: { phone, },
      }),
      /** Проверка смс кода */
      verifications: (phone: string, code: string, passport?: string,) => ({
        ...defaultHeaders,
        url: withHost("/v1/otp/verifications",),
        method: "POST",
        body: { phone, code, passport, },
      }),
    },
    /** Создание заявки на оплату по QR коду */
    paymentRequests: (partnerId: string, pointCode: string,) => ({
      ...defaultHeaders,
      url: withHost("/v1/payment-requests",),
      method: "POST",
      body: {
        partnerId,
        pointCode,
      },
    }),
    token: {
      /** Обновление токена */
      refreshment: () => ({
        ...defaultHeaders,
        url: withHost("/v1/token/refreshment",),
        method: "POST",
      }),
    },
    limits: {
      byId: (limitId: string,) => ({
        /** Запрос на выпуск карты */
        cardIssuance: (body: { email: string },) => ({
          ...defaultHeaders,
          url: withHost(`/v1/limits/${limitId}/payment-card`,),
          method: "POST",
          body,
        }),
        /** Подтверждение выпуска карты */
        confirmation: (body: { otp: string },) => ({
          ...defaultHeaders,
          url: withHost(`/v1/limits/${limitId}/payment-card/confirmation`,),
          method: "POST",
          body,
        }),
        /** Метод получения ссылки на оплату по лимиту */
        paymentLink: (body: { amount: number },) => ({
          ...defaultHeaders,
          url: withHost(`/v1/limits/${limitId}/payment`,),
          method: "POST",
          body,
        }),
      }),
    },
    qr: {
      /** Создание платежа по QR */
      index: (qrPayload: string,) => ({
        ...defaultHeaders,
        url: withHost("/v1/qr/payment",),
        method: "POST",
        body: { qrPayload, },
      }),
      /** Проведение платежа по QR */
      byId: (paymentId: string,) => ({
        payment: (body: { precheckId: string; amount?: number },) => ({
          ...defaultHeaders,
          url: withHost(`/v1/qr/payment/${paymentId}`,),
          method: "POST",
          body,
        }),
      }),
    },
    email: {
      send: (email: string, params?: Record<string, string>,) => ({
        ...defaultHeaders,
        url: withHost("/v1/confirmation-codes",),
        method: "POST",
        body: {
          recipient: email,
          channel: "email",
          ...(params ? { params, } : {}),
        },
      }),
      confirm: (code: string, consents: string[], token: string,) => ({
        ...defaultHeaders,
        url: withHost(`/v1/confirmation-codes/${token}/verifications`,),
        method: "POST",
        body: { code, consents, },
      }),
    },
  },
};
